import { supabase } from '@/lib/supabase';
import { listTauxTva } from '@/modules/catalogue/api';
import {
  computeDevisTotals,
  createDevis,
  type FormLigne,
  type LigneInsert,
  nextDevisNumber,
} from '@/modules/devis/api';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

// ----- Types -----------------------------------------------------------------

export interface QcmModele {
  id: string;
  code: string;
  nom: string;
  description: string | null;
  couleur: string | null;
  version: number;
}
export interface QcmOption {
  id: string;
  code: string;
  libelle: string;
  ordre: number;
}
export interface QcmQuestion {
  id: string;
  code: string;
  libelle: string;
  aide: string | null;
  type: string;
  unite: string | null;
  obligatoire: boolean;
  ordre: number;
  question_options: QcmOption[];
}
export interface QcmSection {
  id: string;
  titre: string;
  description: string | null;
  ordre: number;
  questions: QcmQuestion[];
}
export interface QcmModeleFull extends QcmModele {
  questionnaire_sections: QcmSection[];
}

export type AnswerValue = number | boolean | string | null;
export type Answers = Record<string, AnswerValue>;

// ----- Chargement ------------------------------------------------------------

export async function listQuestionnaires(): Promise<QcmModele[]> {
  const { data, error } = await supabase
    .from('questionnaire_modeles')
    .select('id,code,nom,description,couleur,version')
    .eq('actif', true)
    .order('nom');
  if (error) throw new Error(error.message);
  return (data ?? []) as QcmModele[];
}

export async function getQuestionnaire(id: string): Promise<QcmModeleFull> {
  const { data, error } = await supabase
    .from('questionnaire_modeles')
    .select(
      'id,code,nom,description,couleur,version,questionnaire_sections(id,titre,description,ordre,questions(id,code,libelle,aide,type,unite,obligatoire,ordre,question_options(id,code,libelle,ordre)))',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  const m = data as unknown as QcmModeleFull;
  m.questionnaire_sections.sort((a, b) => a.ordre - b.ordre);
  for (const s of m.questionnaire_sections) {
    s.questions.sort((a, b) => a.ordre - b.ordre);
    for (const q of s.questions) q.question_options.sort((a, b) => a.ordre - b.ordre);
  }
  return m;
}

// ----- Moteur de règles ------------------------------------------------------

type Clause = { questionCode: string; op: 'eq' | 'neq' | 'in' | 'gt' | 'lt'; value: unknown };
type Conditions = { operator: 'AND' | 'OR'; clauses: Clause[] } | null;
interface Regle {
  id: string;
  nom: string;
  conditions: Conditions;
  action: string;
  action_params: {
    libelleOverride?: string;
    libelleFromCode?: string;
    quantiteFormule?: string;
    prixUnitaireFormule?: string;
    tauxTvaCode?: string;
  };
}

export interface GeneratedLine {
  libelle: string;
  quantite: number;
  prixUnitaireHt: number;
  tauxTvaCode: string;
}

function buildVars(answers: Answers): Record<string, number> {
  const vars: Record<string, number> = {};
  for (const [code, v] of Object.entries(answers)) {
    if (typeof v === 'number') vars[code] = v;
    else if (typeof v === 'boolean') vars[code] = v ? 1 : 0;
    else if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
      vars[code] = Number(v);
    }
  }
  return vars;
}

// Évaluateur d'expressions sûr (pas d'eval) : nombres, variables, + - * / ( ).
function evalFormule(expr: string, vars: Record<string, number>): number {
  const tokens = expr.match(/\d+\.?\d*|[a-zA-Z_]\w*|[-+*/()]/g) ?? [];
  const output: number[] = [];
  const ops: string[] = [];
  const prec = (o: string): number => (o === '*' || o === '/' ? 2 : o === '+' || o === '-' ? 1 : 0);
  const apply = (o: string): void => {
    const b = output.pop() ?? 0;
    const a = output.pop() ?? 0;
    output.push(o === '+' ? a + b : o === '-' ? a - b : o === '*' ? a * b : b === 0 ? 0 : a / b);
  };
  for (const t of tokens) {
    if (/^\d/.test(t)) output.push(Number(t));
    else if (/^[a-zA-Z_]/.test(t)) output.push(vars[t] ?? 0);
    else if (t === '(') ops.push(t);
    else if (t === ')') {
      let top = ops[ops.length - 1];
      while (top !== undefined && top !== '(') {
        apply(top);
        ops.pop();
        top = ops[ops.length - 1];
      }
      ops.pop();
    } else {
      let top = ops[ops.length - 1];
      while (top !== undefined && top !== '(' && prec(top) >= prec(t)) {
        apply(top);
        ops.pop();
        top = ops[ops.length - 1];
      }
      ops.push(t);
    }
  }
  let top = ops[ops.length - 1];
  while (top !== undefined) {
    if (top !== '(') apply(top);
    ops.pop();
    top = ops[ops.length - 1];
  }
  return output.pop() ?? 0;
}

function evalConditions(conditions: Conditions, answers: Answers): boolean {
  if (!conditions) return true;
  const results = conditions.clauses.map((c) => {
    const actual = answers[c.questionCode];
    switch (c.op) {
      case 'eq':
        return actual === c.value;
      case 'neq':
        return actual !== c.value;
      case 'gt':
        return Number(actual) > Number(c.value);
      case 'lt':
        return Number(actual) < Number(c.value);
      case 'in':
        return Array.isArray(c.value) && (c.value as unknown[]).includes(actual);
      default:
        return false;
    }
  });
  return conditions.operator === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

async function listRegles(modeleId: string): Promise<Regle[]> {
  const { data, error } = await supabase
    .from('question_regles')
    .select('id,nom,conditions,action,action_params')
    .eq('modele_id', modeleId)
    .eq('actif', true)
    .order('ordre');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Regle[];
}

export function generateLines(regles: Regle[], answers: Answers): GeneratedLine[] {
  const vars = buildVars(answers);
  const lines: GeneratedLine[] = [];
  for (const r of regles) {
    if (r.action !== 'add_quote_line') continue;
    if (!evalConditions(r.conditions, answers)) continue;
    const p = r.action_params;
    // Ligne dont le libellé provient d'une réponse texte : on la saute si vide.
    let libelle = p.libelleOverride ?? r.nom;
    if (p.libelleFromCode) {
      const txt = String(answers[p.libelleFromCode] ?? '').trim();
      if (txt === '') continue;
      libelle = txt;
    }
    const quantite = p.quantiteFormule ? evalFormule(p.quantiteFormule, vars) : 1;
    const prix = p.prixUnitaireFormule ? evalFormule(p.prixUnitaireFormule, vars) : 0;
    if (quantite <= 0) continue;
    lines.push({
      libelle,
      quantite,
      prixUnitaireHt: prix,
      tauxTvaCode: p.tauxTvaCode ?? '20',
    });
  }
  return lines;
}

// ----- Génération du devis depuis les réponses -------------------------------

function isoDay(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

export async function createDevisFromQcm(
  modele: QcmModeleFull,
  answers: Answers,
  contactId: string,
  chantierId: string,
): Promise<string> {
  const [tvaList, regles] = await Promise.all([listTauxTva(), listRegles(modele.id)]);
  const generated = generateLines(regles, answers);
  const tvaByCode = new Map(tvaList.map((t) => [t.code, t]));
  const tvaDefaut = tvaByCode.get('20') ?? tvaList[0];

  // 1. Enregistre l'instance de réponse.
  const { data: repData, error: repErr } = await supabase
    .from('questionnaire_reponses')
    .insert({
      organization_id: ORG_ID,
      modele_id: modele.id,
      modele_version: modele.version,
      contact_id: contactId === '' ? null : contactId,
      statut: 'converti_devis',
      variables_calculees: answers,
    })
    .select('id')
    .single();
  if (repErr) throw new Error(repErr.message);
  const reponseId = (repData as { id: string }).id;

  // 2. Enregistre chaque réponse de question (audit + ré-exploitation).
  const questionByCode = new Map<string, QcmQuestion>();
  for (const s of modele.questionnaire_sections) {
    for (const q of s.questions) questionByCode.set(q.code, q);
  }
  const numericTypes = ['nombre', 'surface_m2', 'lineaire_ml', 'volume_m3'];
  const rqRows: Record<string, unknown>[] = [];
  for (const [code, val] of Object.entries(answers)) {
    const q = questionByCode.get(code);
    if (!q || val === null || val === '') continue;
    const row: Record<string, unknown> = {
      organization_id: ORG_ID,
      reponse_id: reponseId,
      question_id: q.id,
      question_code: code,
    };
    if (q.type === 'booleen') row.valeur_booleen = Boolean(val);
    else if (numericTypes.includes(q.type)) row.valeur_nombre = String(val);
    else if (q.type === 'date') row.valeur_date = String(val);
    else row.valeur_texte = String(val);
    rqRows.push(row);
  }
  if (rqRows.length > 0) {
    const { error } = await supabase.from('reponses_question').insert(rqRows);
    if (error) throw new Error(error.message);
  }

  // 3. Construit les lignes + totaux du devis.
  const formLignes: FormLigne[] = generated.map((g) => ({
    article_id: '',
    libelle: g.libelle,
    // Les lignes du questionnaire naissent d'une formule, pas d'un article :
    // elles n'ont pas de descriptif à reprendre.
    description: '',
    quantite: String(g.quantite),
    unite_id: '',
    prix_unitaire_ht: g.prixUnitaireHt.toFixed(2),
    remise_pourcentage: '0',
    taux_tva_id: (tvaByCode.get(g.tauxTvaCode) ?? tvaDefaut)?.id ?? '',
  }));
  const totals = computeDevisTotals(formLignes, tvaList);
  const lignes: LigneInsert[] = formLignes.map((l, i) => ({
    ordre: i,
    type: 'article',
    article_id: null,
    libelle: l.libelle,
    description: null,
    quantite: String(Number(l.quantite) || 0),
    unite_id: null,
    prix_unitaire_ht: String(Number(l.prix_unitaire_ht) || 0),
    remise_pourcentage: '0',
    taux_tva_id: l.taux_tva_id === '' ? null : l.taux_tva_id,
    total_ht: (totals.ligneTotals[i]?.total_ht ?? 0).toFixed(2),
    total_ttc: (totals.ligneTotals[i]?.total_ttc ?? 0).toFixed(2),
  }));
  const header = {
    contact_id: contactId,
    chantier_id: chantierId === '' ? null : chantierId,
    objet: modele.nom,
    date_emission: isoDay(0),
    date_expiration: isoDay(30),
    statut: 'brouillon',
    montant_ht: totals.montant_ht.toFixed(2),
    montant_tva: totals.montant_tva.toFixed(2),
    montant_ttc: totals.montant_ttc.toFixed(2),
    detail_tva: totals.detail_tva,
  };
  const numero = await nextDevisNumber();
  const devisId = await createDevis(header, lignes, numero, {
    questionnaire_reponse_id: reponseId,
  });

  // 4. Relie la réponse au devis généré.
  await supabase.from('questionnaire_reponses').update({ devis_genere_id: devisId }).eq('id', reponseId);

  return devisId;
}
